/**
 * Rate Correction Admin Page (MoM 16 Apr 2025, item 13).
 *
 * Two tabs:
 *   1. Apply Correction — form to set the displayed rate for a
 *      (industry, division, city, product) tuple.
 *   2. Audit Log        — paginated list of recent corrections across
 *      vote-driven and admin-driven sources.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Form,
  Select,
  Input,
  InputNumber,
  Button,
  Table,
  Tabs,
  Tag,
  Typography,
  Row,
  Col,
  message,
  Alert,
  Grid,
  Empty,
  Radio,
  Tooltip,
} from "antd";
import { InfoCircleOutlined, ThunderboltFilled } from "@ant-design/icons";
import { connect } from "react-redux";
import moment from "moment";
import {
  postAdminRateCorrection,
  getAdminRateCorrections,
} from "../../RestServices/postingService";

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { useBreakpoint } = Grid;

const INDUSTRIES = [
  { value: "EGG_INDUSTRY", label: "Egg Industry", products: ["WHITE_EGG", "WHITE_LAYER"] },
  { value: "BROILER_INDUSTRY", label: "Broiler Industry", products: ["BROILER", "BROILER_CHICK"] },
  { value: "FEED_MATERIAL", label: "Feed Material", products: ["CORN", "SOYABEAN"] },
];

const DIVISIONS = [
  { value: "lahore",      label: "Lahore",      province: "Punjab" },
  { value: "faisalabad",  label: "Faisalabad",  province: "Punjab" },
  { value: "multan",      label: "Multan",      province: "Punjab" },
  { value: "rawalpindi",  label: "Rawalpindi",  province: "Punjab" },
  { value: "gujranwala",  label: "Gujranwala",  province: "Punjab" },
  { value: "sargodha",    label: "Sargodha",    province: "Punjab" },
  { value: "karachi",     label: "Karachi",     province: "Sindh" },
  { value: "hyderabad",   label: "Hyderabad",   province: "Sindh" },
  { value: "peshawar",    label: "Peshawar",    province: "KP" },
  { value: "quetta",      label: "Quetta",      province: "Balochistan" },
];

// Unique province list derived from DIVISIONS. Province-level corrections
// fan out to every division under the chosen province on the backend.
const PROVINCES = Array.from(
  new Set(DIVISIONS.map((d) => d.province))
).map((p) => ({
  value: p,
  label: p,
  divisions: DIVISIONS.filter((d) => d.province === p).map((d) => d.label),
}));

function scopeTag(scope) {
  if (scope === "PROVINCE") return <Tag color="purple">PROVINCE</Tag>;
  if (scope === "DIVISION") return <Tag color="geekblue">DIVISION</Tag>;
  return null;
}

function sourceTag(source) {
  if (source === "ADMIN_OVERRIDE") return <Tag color="gold">ADMIN</Tag>;
  if (source === "VOTE_AGGREGATE") return <Tag color="blue">VOTES</Tag>;
  return <Tag>{source}</Tag>;
}

function sentimentTag(s) {
  if (s === "BULLISH") return <Tag color="green">BULLISH</Tag>;
  if (s === "BEARISH") return <Tag color="red">BEARISH</Tag>;
  if (s === "NEUTRAL") return <Tag>NEUTRAL</Tag>;
  return null;
}

function RateCorrectionPage({ Auth }) {
  const token = Auth?.user?.token;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [filterIndustry, setFilterIndustry] = useState("EGG_INDUSTRY");
  const [selectedIndustry, setSelectedIndustry] = useState("EGG_INDUSTRY");
  // DIVISION = single-division correction. PROVINCE = fan-out across all
  // divisions in the chosen province (backend takes care of the writes).
  //
  // PROVINCE is the default (Aug 2026): the app surfaces rates, pressure and
  // the AI insight at PROVINCE level, and AdminRateBaselines — the collection
  // /forecast, /weekly-chart and /ai-insight actually read — is keyed by
  // province. A DIVISION-scoped correction writes an audit row and a
  // province baseline anyway, so scoping to a single division only makes the
  // audit log narrower without changing what users see. Division scope is
  // kept for targeted fixes.
  const [scope, setScope] = useState("PROVINCE");
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const products = useMemo(() => {
    const industry = INDUSTRIES.find((i) => i.value === selectedIndustry);
    return industry ? industry.products : [];
  }, [selectedIndustry]);

  const loadList = async () => {
    if (!token) return;
    setListLoading(true);
    try {
      const data = await getAdminRateCorrections(
        { industry: filterIndustry, limit: 100 },
        token
      );
      setItems(data.items || []);
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to fetch corrections");
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndustry]);

  const onSubmit = async (values) => {
    if (!token) return;
    setLoading(true);
    try {
      // Payload shape (matches posts-service v2):
      //   - industry, product, correctedRate  → always required.
      //   - DIVISION scope: `division` (+ optional city).
      //   - PROVINCE scope: `province` only (backend fans out to every
      //     division in that province; `division`/`city` stay null).
      //   - `notes`   → user-visible narrative that OVERRIDES the
      //                 AI-insight text until the correction ages out.
      //   - `reason`  → admin-only audit label.
      const payload = {
        industry:      values.industry,
        product:       values.product,
        correctedRate: Number(values.correctedRate),
        reason:        values.reason || null,
        notes:         values.notes  || null,
        // AI-insight overrides — admin-authored narrative that replaces
        // the Gemini-generated text on /ai-insight for this industry until
        // the day rolls over. Either field is independently optional.
        // Empty string explicitly clears a previously-set value; undefined
        // means "leave the existing stored value alone".
        ...(values.aiInsightText !== undefined
              ? { aiInsightText: values.aiInsightText || "" } : {}),
        ...(values.aiInsightUrdu !== undefined
              ? { aiInsightUrdu: values.aiInsightUrdu || "" } : {}),
      };
      if (scope === "PROVINCE") {
        payload.province = values.province;
        payload.division = null;
        payload.city     = null;
      } else {
        payload.division = values.division;
        payload.city     = values.city || null;
      }

      // Optional pressure seeds — only include fields the admin actually
      // filled in. The backend treats missing fields as "compute normally".
      const overrides = {};
      if (values.buyingPressure  != null && values.buyingPressure  !== "") overrides.buyingPressure  = Number(values.buyingPressure);
      if (values.sellingPressure != null && values.sellingPressure !== "") overrides.sellingPressure = Number(values.sellingPressure);
      if (values.marketSide) overrides.marketSide = values.marketSide;
      if (Object.keys(overrides).length) payload.overrides = overrides;

      const resp = await postAdminRateCorrection(payload, token);
      const correction = resp?.correction || {};
      if (correction.scope === "PROVINCE" && Array.isArray(correction.fannedOutTo)) {
        message.success(
          `Province-wide reset applied across ${correction.fannedOutTo.length} division(s): ` +
            correction.fannedOutTo.join(", ")
        );
      } else {
        message.success("Rate correction applied and broadcast to users.");
      }
      if (values.notes) {
        message.info("Admin notes will override the AI insight card for this industry/region.", 6);
      }

      form.resetFields();
      form.setFieldsValue({ industry: "EGG_INDUSTRY" });
      setSelectedIndustry("EGG_INDUSTRY");
      setScope("DIVISION");
      loadList();
    } catch (e) {
      message.error(
        e?.response?.data?.message ||
        (e?.response?.status
          ? `Failed to apply rate correction (HTTP ${e.response.status}).`
          : "Failed to apply rate correction — could not reach the server.")
      );
    } finally {
      setLoading(false);
    }
  };

  // ── Table columns ────────────────────────────────────────────────────────────
  const columns = [
    {
      title: "When",
      dataIndex: "createdAt",
      width: 130,
      fixed: isMobile ? false : "left",
      render: (v) => (
        <div style={{ fontSize: 13, lineHeight: 1.2 }}>
          <span style={{ fontWeight: 600 }}>{moment(v).format("D MMM")}</span>
          <span style={{ fontSize: 11, color: "#8c8c8c", marginLeft: 6 }}>
            {moment(v).format("HH:mm")}
          </span>
        </div>
      ),
    },
    { title: "Source", dataIndex: "source", width: 120, render: sourceTag },
    { title: "Industry", dataIndex: "industry", width: 180, render: (v) => (v || "").replace(/_/g, " ") },
    {
      title: "Location", width: 250, render: (_, r) => (
        <div style={{ fontSize: 13 }}>
          {r.division ? r.division[0].toUpperCase() + r.division.slice(1) : "—"}
          {r.city ? <span style={{ color: "#8c8c8c", fontSize: 12 }}> / {r.city}</span> : ""}
        </div>
      )
    },
    { title: "Product", dataIndex: "product", width: 160, render: (v) => (v || "").replace(/_/g, " ") },
    {
      title: "Announced",
      dataIndex: "announcedRate",
      width: 140,
      align: "right",
      render: (v) => (v == null ? "—" : `Rs.${v}`),
    },
    {
      title: "Community",
      dataIndex: "communityRate",
      align: "right",
      render: (v) => (v == null ? "—" : `Rs.${v}`),
    },
    {
      title: "Corrected",
      dataIndex: "correctedRate",
      width: 140,
      align: "right",
      render: (v) => (
        <Text strong style={{ color: "#D45216", fontSize: 14 }}>
          {v == null ? "—" : `Rs.${v}`}
        </Text>
      ),
    },
    { title: "Sentiment", dataIndex: "sentiment", width: 130, render: sentimentTag },
    {
      title: "By",
      dataIndex: "actorId",
      width: 150,
      ellipsis: true,
      render: (v) => (
        <Text title={v} style={{ fontSize: 15, color: "#8c8c8c" }}>
          {v ? `...${v.slice(-4)}` : "—"}
        </Text>
      )
    },
    {
      title: "Reason",
      dataIndex: "reason",
      width: 350,
      render: (v) => (
        <div style={{
          whiteSpace: "normal",
          wordBreak: "break-word",
          fontSize: 15,
          color: "#595959",
          lineHeight: 1.4
        }}>
          {v || "—"}
        </div>
      )
    },
  ];

  // ── Mobile card view for audit log ───────────────────────────────────────────
  const MobileAuditCard = ({ item }) => (
    <div
      style={{
        background: "white",
        marginBottom: 10,
        borderRadius: 12,
        padding: 14,
        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
        border: "1px solid #f0f0f0",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: "#262626" }}>
            {(item.product || "").replace(/_/g, " ")}
          </span>
          {sourceTag(item.source)}
        </div>
        <div style={{ fontSize: 11, color: "#bfbfbf" }}>
          {moment(item.createdAt).format("D MMM, HH:mm")}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          marginBottom: 8,
        }}
      >
        <div>
          <div style={{ fontSize: 12, color: "#8c8c8c" }}>
            {item.division
              ? item.division[0].toUpperCase() + item.division.slice(1)
              : "—"}
            {item.city ? (
              <span style={{ color: "#bfbfbf" }}> / {item.city}</span>
            ) : (
              ""
            )}
          </div>
          <div style={{ fontSize: 11, color: "#bfbfbf", marginTop: 2 }}>
            Old: {item.announcedRate || "—"} · Comm:{" "}
            {item.communityRate || "—"}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div
            style={{
              fontSize: 10,
              color: "#8c8c8c",
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            Corrected
          </div>
          <div
            style={{
              fontSize: 18,
              fontWeight: 800,
              color: "#D45216",
              lineHeight: 1,
            }}
          >
            Rs.{item.correctedRate}
          </div>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingTop: 8,
          borderTop: "1px dashed #f0f0f0",
        }}
      >
        {sentimentTag(item.sentiment)}
        <span style={{ fontSize: 11, color: "#bfbfbf" }}>
          By: {item.actorId || "System"}
        </span>
      </div>

      {item.notes && (
        <div
          style={{
            marginTop: 10,
            padding: "8px 10px",
            background: "#FFF7E6",
            border: "1px solid #FFD591",
            borderRadius: 6,
            fontSize: 12,
            color: "#6E1B06",
          }}
        >
          <strong>User-visible note (overrides AI):</strong> {item.notes}
        </div>
      )}
      {item.reason && (
        <div
          style={{
            marginTop: 8,
            fontSize: 12,
            color: "#595959",
            background: "#fafafa",
            padding: "6px 10px",
            borderRadius: 6,
          }}
        >
          {item.reason}
        </div>
      )}
    </div>
  );


  return (
    <div style={{ padding: isMobile ? 12 : 24, maxWidth: 1400, margin: "0 auto" }}>
      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: 16 }}>
        <Title level={isMobile ? 4 : 3} style={{ color: "#fff", margin: 0 }}>
          Rate Correction
        </Title>
        <Text style={{ color: "#ccc", fontSize: isMobile ? 12 : 14 }}>
          Override today&rsquo;s displayed rate at division or province scope.
          Optional admin notes replace the AI-insight card on user devices.
          Changes are pushed live to subscribed clients and audited below.
        </Text>
      </div>

      <Alert
        showIcon
        style={{ marginBottom: 16, borderRadius: 10 }}
        type="info"
        message="Rate correction — how scopes + notes behave"
        description={
          !isMobile && (
            <div style={{ lineHeight: 1.6 }}>
              <b>Division</b> scope corrects a single division (city optional).{" "}
              <b>Province</b> scope fans out across every division in the
              province and also <b>resets</b> the dashboard&rsquo;s buying /
              selling pressure for users in that region (signals get measured
              from the correction timestamp onward). <b>Notes</b> are shown to
              users and <b>override the AI insight card</b> until the
              correction ages out of today.
            </div>
          )
        }
      />

      <Tabs
        defaultActiveKey="apply"
        size={isMobile ? "small" : "middle"}
        tabBarStyle={{ marginBottom: 16 }}
      >
        {/* ── APPLY CORRECTION ─────────────────────────────────────────── */}
        <TabPane tab="Apply Correction" key="apply">
          <Card
            bordered={false}
            style={{
              borderRadius: 14,
              boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
              maxWidth: 760,
            }}
            bodyStyle={{ padding: isMobile ? 16 : 28 }}
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={onSubmit}
              // Province defaults to Punjab to match the default PROVINCE
              // scope — it's the only province live in v1 and the one the
              // app's rate/insight cards are drawn for.
              initialValues={{ industry: "EGG_INDUSTRY", province: "Punjab" }}
              requiredMark="optional"
            >
              {/* Section: Target */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 16,
                }}
              >
                <span
                  style={{
                    width: 4,
                    height: 16,
                    background: "#D45216",
                    borderRadius: 2,
                  }}
                />
                <Text strong style={{ fontSize: 13, color: "#6E1B06", textTransform: "uppercase", letterSpacing: 0.6 }}>
                  Target
                </Text>
              </div>

              <Row gutter={[16, 0]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Industry"
                    name="industry"
                    rules={[{ required: true, message: "Industry is required" }]}
                  >
                    <Select
                      size="large"
                      onChange={(v) => {
                        setSelectedIndustry(v);
                        form.setFieldsValue({ product: undefined });
                      }}
                    >
                      {INDUSTRIES.map((i) => (
                        <Select.Option key={i.value} value={i.value}>
                          {i.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Product"
                    name="product"
                    rules={[{ required: true, message: "Product is required" }]}
                  >
                    <Select size="large" placeholder="Select product">
                      {products.map((p) => (
                        <Select.Option key={p} value={p}>
                          {p.replace(/_/g, " ")}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              {/* Scope toggle — DIVISION (single-division) vs PROVINCE
                   (fan-out). PROVINCE scope treats the correction as a
                   market-wide reset across every division inside the
                   chosen province, which also resets the dashboard
                   buying/selling pressure on the consumer side. */}
              <Form.Item label="Scope" required>
                <Radio.Group
                  value={scope}
                  onChange={(e) => {
                    setScope(e.target.value);
                    // Clear mutually-exclusive fields when toggling. Coming
                    // back to PROVINCE restores the Punjab default so the
                    // form is submittable without an extra click.
                    if (e.target.value === "PROVINCE") {
                      form.setFieldsValue({
                        division: undefined,
                        city: undefined,
                        province: form.getFieldValue("province") || "Punjab",
                      });
                    } else {
                      form.setFieldsValue({ province: undefined });
                    }
                  }}
                  size="large"
                >
                  <Radio.Button value="DIVISION">
                    Division
                    <Text type="secondary" style={{ marginLeft: 6, fontSize: 12 }}>
                      (single)
                    </Text>
                  </Radio.Button>
                  <Radio.Button value="PROVINCE">
                    <ThunderboltFilled style={{ marginRight: 4 }} />
                    Province
                    <Text type="secondary" style={{ marginLeft: 6, fontSize: 12 }}>
                      (market-wide reset)
                    </Text>
                  </Radio.Button>
                </Radio.Group>
              </Form.Item>

              {scope === "DIVISION" ? (
                <Row gutter={[16, 0]}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Division"
                      name="division"
                      rules={[{ required: true, message: "Division is required" }]}
                    >
                      <Select size="large" placeholder="Select division">
                        {DIVISIONS.map((d) => (
                          <Select.Option key={d.value} value={d.value}>
                            {d.label}
                            <Text type="secondary" style={{ marginLeft: 6, fontSize: 12 }}>
                              ({d.province})
                            </Text>
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="City"
                      name="city"
                    >
                      <Input size="large" placeholder="e.g. Lahore" />
                    </Form.Item>
                  </Col>
                </Row>
              ) : (
                <Row gutter={[16, 0]}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Province"
                      name="province"
                      rules={[{ required: true, message: "Province is required" }]}
                      extra={
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Correction will fan out to every division in this province.
                        </Text>
                      }
                    >
                      <Select size="large" placeholder="Select province">
                        {PROVINCES.map((p) => (
                          <Select.Option key={p.value} value={p.value}>
                            {p.label}
                            <Text type="secondary" style={{ marginLeft: 6, fontSize: 12 }}>
                              ({p.divisions.length} divisions)
                            </Text>
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              )}

              {/* Section: Rate */}
              <div
                style={{
                  borderTop: "1px solid #f0f0f0",
                  marginTop: 22,
                  paddingTop: 18,
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 14,
                }}
              >
                <span
                  style={{
                    width: 4,
                    height: 16,
                    background: "#D45216",
                    borderRadius: 2,
                  }}
                />
                <Text strong style={{ fontSize: 13, color: "#6E1B06", textTransform: "uppercase", letterSpacing: 0.6 }}>
                  Correction
                </Text>
              </div>

              <Row gutter={[16, 0]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Corrected Rate (Rs.)"
                    name="correctedRate"
                    rules={[
                      { required: true, message: "Corrected rate is required" },
                      {
                        validator: (_, value) =>
                          value != null && value > 0
                            ? Promise.resolve()
                            : Promise.reject(new Error("Rate must be > 0")),
                      },
                    ]}
                  >
                    <InputNumber
                      size="large"
                      min={0}
                      step={0.25}
                      style={{ width: "100%" }}
                      placeholder="e.g. 228.50"
                      addonBefore="Rs."
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Reason — admin-only, stays in the audit log tooltip. */}
              <Form.Item
                label={
                  <span>
                    Reason{" "}
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      (admin audit, not shown to users)
                    </Text>
                  </span>
                }
                name="reason"
              >
                <Input
                  size="large"
                  placeholder="e.g. Mandi snapped after announcement"
                />
              </Form.Item>

              {/* Notes — quick one-line override (max 500 chars). Auto-
                   detected as Urdu or English based on script. */}
              <Form.Item
                label={
                  <Tooltip title="Quick note shown to users — overrides the AI-insight card for this industry/region until the correction ages out. Use the AI Insight section below for full English + Urdu narratives.">
                    <span>
                      <InfoCircleOutlined style={{ marginRight: 6, color: "#D45216" }} />
                      Quick note (overrides AI insight)
                    </span>
                  </Tooltip>
                }
                name="notes"
              >
                <Input.TextArea
                  rows={2}
                  maxLength={500}
                  showCount
                  placeholder={
                    "e.g. Rates reset across the province — verify before quoting.\n" +
                    "یا: منڈی میں تیزی آ گئی، خریدار احتیاط سے چلیں۔"
                  }
                />
              </Form.Item>

              {/* ── AI Insight (English / Urdu) ──────────────────────────
                   Replaces the Gemini-generated text on /ai-insight for
                   this industry until the day rolls over. Both fields are
                   independent — leave blank to let the AI write its own
                   narrative. */}
              <div
                style={{
                  marginTop: 22,
                  padding: "16px 18px",
                  background: "#FFF8F0",
                  border: "1px solid #FFE2C2",
                  borderRadius: 10,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "space-between",
                    marginBottom: 12,
                    flexWrap: "wrap",
                    gap: 6,
                  }}
                >
                  <Text strong style={{ fontSize: 13, color: "#6E1B06", textTransform: "uppercase", letterSpacing: 0.5 }}>
                    AI Insight Override
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Leave both blank → AI writes its own narrative.
                  </Text>
                </div>

                <Row gutter={[16, 12]}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="English narrative"
                      name="aiInsightText"
                      style={{ marginBottom: 0 }}
                    >
                      <Input.TextArea
                        rows={4}
                        maxLength={1000}
                        showCount
                        placeholder="e.g. Egg supply tightening across Punjab — expect mild upward pressure tomorrow."
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="اردو narrative"
                      name="aiInsightUrdu"
                      style={{ marginBottom: 0 }}
                    >
                      <Input.TextArea
                        rows={4}
                        maxLength={1000}
                        showCount
                        placeholder="مثال: انڈے کی سپلائی کم ہو رہی ہے — کل ریٹ تھوڑا اوپر جانے کا امکان ہے۔"
                        style={{ direction: "rtl", textAlign: "right" }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              {/* ── Optional pressure seeds ─────────────────────────────
                   When set, the dashboard's buying / selling pressure and
                   market side reset to these values at the correction
                   timestamp. Post-reset posts + votes drift from here.
                   Leave blank to let the service compute normally. */}
              <div
                style={{
                  borderTop: "1px solid #f0f0f0",
                  marginTop: 22,
                  paddingTop: 18,
                  marginBottom: 12,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span
                    style={{
                      width: 4,
                      height: 16,
                      background: "#D45216",
                      borderRadius: 2,
                    }}
                  />
                  <Text strong style={{ fontSize: 13, color: "#6E1B06", textTransform: "uppercase", letterSpacing: 0.6 }}>
                    Reset Pressures
                  </Text>
                </div>
                <div style={{ marginTop: 4, marginLeft: 14 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Leave blank to let the dashboard compute from posts + votes.
                  </Text>
                </div>
              </div>

              <Row gutter={[16, 0]}>
                <Col xs={24} md={8}>
                  <Form.Item
                    label="Buying pressure"
                    name="buyingPressure"
                    extra={<span style={{ fontSize: 11 }}>numeric</span>}
                  >
                    <InputNumber
                      size="large"
                      min={0}
                      style={{ width: "100%" }}
                      placeholder="e.g. 12"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    label="Selling pressure"
                    name="sellingPressure"
                    extra={<span style={{ fontSize: 11 }}>ratio (×)</span>}
                  >
                    <InputNumber
                      size="large"
                      min={0}
                      step={0.1}
                      style={{ width: "100%" }}
                      placeholder="e.g. 2.5"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label="Market side" name="marketSide">
                    <Select size="large" allowClear placeholder="Auto-detect">
                      <Select.Option value="BUYER_HEAVY">BUYER_HEAVY</Select.Option>
                      <Select.Option value="SELLER_HEAVY">SELLER_HEAVY</Select.Option>
                      <Select.Option value="BALANCED">BALANCED</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  size="large"
                  block={isMobile}
                  style={{
                    background: "#D45216",
                    borderColor: "#D45216",
                    fontWeight: 600,
                    minWidth: isMobile ? undefined : 180,
                  }}
                >
                  {scope === "PROVINCE" ? "Apply Province-Wide Reset" : "Apply Correction"}
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </TabPane>

        {/* ── AUDIT LOG ────────────────────────────────────────────────── */}
        <TabPane tab="Audit Log" key="audit">
          <Card
            bordered={false}
            style={{
              borderRadius: 14,
              boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
            }}
            bodyStyle={{ padding: isMobile ? 12 : 20 }}
          >
            <Row gutter={[12, 12]} align="middle" style={{ marginBottom: 16 }}>
              <Col xs={24} sm={12} md={8}>
                <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>
                  Filter by industry
                </div>
                <Select
                  value={filterIndustry}
                  onChange={setFilterIndustry}
                  size="large"
                  style={{ width: "100%" }}
                >
                  {INDUSTRIES.map((i) => (
                    <Select.Option key={i.value} value={i.value}>
                      {i.label}
                    </Select.Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Button
                  onClick={loadList}
                  loading={listLoading}
                  size="large"
                  block
                  style={{ marginTop: isMobile ? 0 : 18 }}
                >
                  Refresh
                </Button>
              </Col>
              <Col xs={24} md={10} style={{ textAlign: isMobile ? "left" : "right" }}>
                <Text type="secondary">
                  {items.length} correction{items.length === 1 ? "" : "s"} shown
                </Text>
              </Col>
            </Row>

            {isMobile ? (
              // Mobile: card list
              listLoading ? (
                <div style={{ textAlign: "center", padding: 40 }}>Loading…</div>
              ) : items.length === 0 ? (
                <Empty description="No corrections yet" />
              ) : (
                items.map((item) => <MobileAuditCard key={item._id} item={item} />)
              )
            ) : (
              // Desktop: full table
              <Table
                rowKey={(r) => r._id}
                dataSource={items}
                columns={columns}
                loading={listLoading}
                pagination={{ pageSize: 15, showSizeChanger: false }}
                scroll={{ x: 2000 }}
                size="small"
                locale={{ emptyText: <Empty description="No corrections yet" /> }}
              />
            )}
          </Card>
        </TabPane>
      </Tabs>
    </div>
  );
}

const mapStateToProps = (store) => ({
  Auth: store.Authentication,
});

export default connect(mapStateToProps)(RateCorrectionPage);
